import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  UploadSimple, MagnifyingGlass, Trash, CheckCircle, XCircle, Clock, ArrowClockwise,
  Eye, EyeSlash, Receipt, Warning, Lightning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  success: { icon: CheckCircle, cls: 'bg-emerald-100 text-emerald-800' },
  pending: { icon: Clock, cls: 'bg-amber-100 text-amber-800' },
  fail:    { icon: XCircle, cls: 'bg-rose-100 text-rose-800' },
  unknown: { icon: Warning, cls: 'bg-gray-100 text-gray-700' },
};

const ENTRY_TYPES = [
  { key: 'opening_balance', label: 'Opening Balance' },
  { key: 'payment_received', label: 'Payment Received' },
  { key: 'adjustment_plus', label: 'Adjustment (+)' },
  { key: 'adjustment_minus', label: 'Adjustment (−)' },
];

function fmt(n) {
  const v = Number(n || 0);
  return '₹' + v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return s; }
}

export default function ElectricityBulkSection({ vendorId, vendorName, onLedgerChanged }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadDate, setUploadDate] = useState(new Date().toISOString().slice(0, 10));
  const [uploadRemarks, setUploadRemarks] = useState('');
  const [uploadRowFilter, setUploadRowFilter] = useState('all');
  const [uploadRowSearch, setUploadRowSearch] = useState('');
  const resetUploadDialog = useCallback(() => {
    setPreviewData(null);
    setUploadFile(null);
    setUploadRowFilter('all');
    setUploadRowSearch('');
  }, []);
  const [browseBatch, setBrowseBatch] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowSearch, setRowSearch] = useState('');
  const [rowSearchInput, setRowSearchInput] = useState('');
  const [rowStatus, setRowStatus] = useState('all');
  const [showDelete, setShowDelete] = useState(false);
  const [refundRow, setRefundRow] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '' });
  const [refunding, setRefunding] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalHits, setGlobalHits] = useState(null);
  const [consumerCheck, setConsumerCheck] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [manualForm, setManualForm] = useState({ entry_type: 'payment_received', amount: '', entry_date: new Date().toISOString().slice(0, 10), note: '' });
  const [posting, setPosting] = useState(false);
  const previewAbort = useRef(null);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/electricity-bulk/batches/${vendorId}`);
      setBatches(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load batches');
    } finally { setLoading(false); }
  }, [vendorId]);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  // Debounce row search
  useEffect(() => {
    const t = setTimeout(() => setRowSearch(rowSearchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [rowSearchInput]);

  const doPreview = async (f) => {
    if (!f) return;
    if (previewAbort.current) previewAbort.current.abort();
    const ac = new AbortController();
    previewAbort.current = ac;
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post(`/electricity-bulk/preview/${vendorId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, signal: ac.signal,
      });
      setPreviewData(r.data);
    } catch (err) {
      if (err?.name !== 'CanceledError') toast.error(err?.response?.data?.detail || 'Preview failed');
    }
  };

  const submitUpload = async () => {
    if (!uploadFile) return toast.error('File select karo');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      if (uploadDate) fd.append('upload_date', uploadDate);
      if (uploadRemarks) fd.append('remarks', uploadRemarks);
      const r = await api.post(`/electricity-bulk/upload/${vendorId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${r.data.message}. Balance: ${fmt(r.data.new_balance)}`);
      setUploadOpen(false); setPreviewData(null); setUploadFile(null); setUploadRemarks('');
      await loadBatches();
      onLedgerChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const loadRows = useCallback(async () => {
    if (!browseBatch) return;
    setRowsLoading(true);
    try {
      const params = new URLSearchParams();
      if (rowSearch) params.set('q', rowSearch);
      if (rowStatus && rowStatus !== 'all') params.set('status', rowStatus);
      const r = await api.get(`/electricity-bulk/batch/${browseBatch.id}/rows?${params.toString()}`);
      setRows(r.data || []);
    } catch (err) {
      toast.error('Failed to load rows');
    } finally { setRowsLoading(false); }
  }, [browseBatch, rowSearch, rowStatus]);

  useEffect(() => { if (browseBatch) loadRows(); }, [browseBatch, loadRows]);

  const doRefund = async () => {
    if (!refundRow || !refundForm.reason || refundForm.reason.length < 2) return toast.error('Reason zaroori hai');
    setRefunding(true);
    try {
      const r = await api.post(`/electricity-bulk/row/${refundRow.id}/refund`, {
        refund_amount: refundForm.amount ? Number(refundForm.amount) : null,
        reason: refundForm.reason,
      });
      toast.success(`${r.data.message}. Balance: ${fmt(r.data.new_balance)}`);
      setRefundRow(null); setRefundForm({ amount: '', reason: '' });
      await loadRows();
      onLedgerChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refund failed');
    } finally { setRefunding(false); }
  };

  const deleteRow = async (r) => {
    if (!window.confirm(`Ye row (TxnID: ${r.txn_id}) delete karein? Ledger pe koi impact nahi hoga.`)) return;
    try {
      await api.delete(`/electricity-bulk/row/${r.id}`);
      toast.success('Row deleted');
      await loadRows();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };

  const deleteBatch = async (b) => {
    if (!window.confirm(`Ye pura batch "${b.file_name}" delete karein? Iske ledger entries reverse ho jayengi (audit trail preserved).`)) return;
    try {
      const r = await api.delete(`/electricity-bulk/batch/${b.id}`);
      toast.success(`${r.data.message}. Balance: ${fmt(r.data.new_balance)}`);
      await loadBatches();
      onLedgerChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };

  const runGlobalSearch = async () => {
    const q = globalSearch.trim();
    if (q.length < 2) return toast.error('Kam se kam 2 chars');
    try {
      const r = await api.get(`/electricity-bulk/search/${vendorId}?q=${encodeURIComponent(q)}`);
      setGlobalHits(r.data || []);
      if (!r.data?.length) toast.info('Koi match nahi mila');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Search failed'); }
  };

  const runConsumerCheck = async () => {
    const q = consumerCheck.trim();
    if (!q) return;
    try {
      const r = await api.get(`/electricity-bulk/consumer-check/${vendorId}?consumer_id=${encodeURIComponent(q)}`);
      setCheckResult(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Check failed'); }
  };

  const postManual = async () => {
    const amt = Number(manualForm.amount);
    if (!amt || amt <= 0) return toast.error('Valid amount dalo');
    setPosting(true);
    try {
      const r = await api.post(`/electricity-bulk/manual-entry/${vendorId}`, manualForm);
      toast.success(`${r.data.message}. Balance: ${fmt(r.data.new_balance)}`);
      setManualForm({ ...manualForm, amount: '', note: '' });
      onLedgerChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Post failed');
    } finally { setPosting(false); }
  };

  const summary = useMemo(() => {
    const s = { credit: 0, debit: 0, batches: batches.length };
    batches.forEach((b) => { s.credit += b.credit_amount; s.debit += b.debit_amount; });
    return s;
  }, [batches]);

  return (
    <div className="space-y-4" data-testid="electricity-bulk-section">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-bold text-gray-900 flex items-center gap-2 text-lg">
            <Lightning size={20} weight="fill" className="text-amber-500" /> Bulk Transaction Uploads
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Excel/CSV files upload karo — Success/Pending amount credit hoga, Fail debit hoga. Har row track hoti hai for future complaint refunds.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setUploadOpen(true)} className="bg-amber-500 hover:bg-amber-600 gap-1" data-testid="bulk-upload-btn">
            <UploadSimple size={14} weight="bold" /> Bulk Upload
          </Button>
        </div>
      </div>

      {/* Summary + Consumer complaint check */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="text-[10px] uppercase font-bold text-emerald-700">Total Credited (S+P)</div>
          <div className="text-xl font-bold text-emerald-800 mt-0.5">{fmt(summary.credit)}</div>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
          <div className="text-[10px] uppercase font-bold text-rose-700">Total Failed Debit</div>
          <div className="text-xl font-bold text-rose-800 mt-0.5">{fmt(summary.debit)}</div>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
          <div className="text-[10px] uppercase font-bold text-indigo-700">Batches Uploaded</div>
          <div className="text-xl font-bold text-indigo-800 mt-0.5">{summary.batches}</div>
        </div>
      </div>

      {/* Manual Entry (Distributor-style) */}
      <div className="bg-white border rounded-xl p-4 space-y-2" data-testid="manual-entry-card">
        <div className="text-xs uppercase font-bold text-gray-500">Add Manual Entry</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <select value={manualForm.entry_type} onChange={(e) => setManualForm({ ...manualForm, entry_type: e.target.value })} className="h-10 px-3 border rounded-md bg-white text-sm" data-testid="manual-entry-type">
            {ENTRY_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <Input type="number" step="0.01" placeholder="Amount" value={manualForm.amount} onChange={(e) => setManualForm({ ...manualForm, amount: e.target.value })} data-testid="manual-entry-amount" />
          <Input type="date" max={new Date().toISOString().slice(0, 10)} value={manualForm.entry_date} onChange={(e) => setManualForm({ ...manualForm, entry_date: e.target.value })} data-testid="manual-entry-date" />
          <Input placeholder="Note (e.g. 'Cash Rs 5000 rcvd 03-Jul')" value={manualForm.note} onChange={(e) => setManualForm({ ...manualForm, note: e.target.value })} data-testid="manual-entry-note" />
          <Button onClick={postManual} disabled={posting} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="manual-entry-post">
            {posting ? 'Posting…' : '+ Post'}
          </Button>
        </div>
      </div>

      {/* Global search + Consumer check */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border rounded-lg p-3">
          <Label className="text-xs">Global Row Search (TxnID / Mobile / Consumer / Operator)</Label>
          <div className="flex gap-2 mt-1">
            <Input value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} placeholder="Search across all batches…" data-testid="bulk-global-search" />
            <Button variant="outline" onClick={runGlobalSearch} data-testid="bulk-global-search-btn"><MagnifyingGlass size={14} /></Button>
          </div>
          {globalHits && (
            <div className="mt-2 text-xs">
              <div className="text-gray-600 font-semibold">{globalHits.length} matches</div>
              <div className="max-h-40 overflow-y-auto mt-1 space-y-1">
                {globalHits.map((h) => (
                  <div key={h.id} className="flex justify-between border rounded p-1.5 hover:bg-gray-50">
                    <div>
                      <div className="font-mono text-[10px]">{h.txn_id}</div>
                      <div className="text-[10px] text-gray-500">{h.operator} · {h.mobile}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{fmt(h.amount)}</div>
                      <StatusChip status={h.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="bg-white border rounded-lg p-3" data-testid="consumer-check-card">
          <Label className="text-xs">Consumer Complaint Gate</Label>
          <div className="flex gap-2 mt-1">
            <Input value={consumerCheck} onChange={(e) => setConsumerCheck(e.target.value)} placeholder="Enter Consumer ID / Mobile…" data-testid="consumer-check-input" />
            <Button variant="outline" onClick={runConsumerCheck} data-testid="consumer-check-btn">Check</Button>
          </div>
          {checkResult && (
            <div className={`mt-2 text-xs rounded p-2 ${
              checkResult.eligible ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                : checkResult.reason === 'already_adjusted' ? 'bg-rose-50 border border-rose-200 text-rose-900'
                : 'bg-amber-50 border border-amber-200 text-amber-900'
            }`}>
              {checkResult.eligible && <>✅ Match found — complaint eligible. TxnID: <b>{checkResult.row.txn_id}</b>, ₹{checkResult.row.amount} ({checkResult.row.status})</>}
              {!checkResult.eligible && checkResult.reason === 'already_adjusted' && <>🔒 Already adjusted on <b>{fmtDate(checkResult.adjusted_on)}</b> by <b>{checkResult.adjusted_by}</b>. Same consumer ki dobara complaint nahi ho sakti.</>}
              {!checkResult.eligible && checkResult.reason === 'no_match' && <>❌ Hamare records me ye consumer nahi mila. Party ko sahi details dalne bolo.</>}
            </div>
          )}
        </div>
      </div>

      {/* Batches table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="flex justify-between items-center p-3 border-b">
          <div className="text-sm font-bold text-gray-700">Uploaded Batches</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowDelete((s) => !s)} data-testid="toggle-show-delete">
              {showDelete ? <EyeSlash size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />} {showDelete ? 'Hide Delete' : 'Show Delete'}
            </Button>
            <Button variant="outline" size="sm" onClick={loadBatches}><ArrowClockwise size={14} /></Button>
          </div>
        </div>
        {loading ? <div className="p-6 text-center text-sm text-gray-500">Loading…</div> :
         batches.length === 0 ? <div className="p-6 text-center text-sm text-gray-500">Abhi tak koi batch upload nahi hui</div> :
        (<div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">File</th>
                <th className="px-3 py-2 text-left">Uploaded</th>
                <th className="px-3 py-2 text-right">Rows</th>
                <th className="px-3 py-2 text-right">Success ✓</th>
                <th className="px-3 py-2 text-right">Pending ⏳</th>
                <th className="px-3 py-2 text-right">Fail ✗</th>
                <th className="px-3 py-2 text-right">Credit</th>
                <th className="px-3 py-2 text-right">Debit</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className="border-t hover:bg-gray-50" data-testid={`batch-row-${b.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-mono text-[11px] font-bold">{b.file_name}</div>
                    <div className="text-[10px] text-gray-500">by {b.uploaded_by_name}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{fmtDate(b.uploaded_at)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{b.total_rows}</td>
                  <td className="px-3 py-2 text-right text-emerald-700">{b.success_count}<br /><span className="text-[10px]">{fmt(b.success_amount)}</span></td>
                  <td className="px-3 py-2 text-right text-amber-700">{b.pending_count}<br /><span className="text-[10px]">{fmt(b.pending_amount)}</span></td>
                  <td className="px-3 py-2 text-right text-rose-700">{b.fail_count}<br /><span className="text-[10px]">{fmt(b.fail_amount)}</span></td>
                  <td className="px-3 py-2 text-right font-bold text-emerald-700">{fmt(b.credit_amount)}</td>
                  <td className="px-3 py-2 text-right font-bold text-rose-700">{fmt(b.debit_amount)}</td>
                  <td className="px-3 py-2 text-right flex gap-1 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setBrowseBatch(b)} data-testid={`batch-view-${b.id}`}>View</Button>
                    {showDelete && (
                      <Button size="sm" variant="outline" onClick={() => deleteBatch(b)} className="text-rose-600 border-rose-300 hover:bg-rose-50" data-testid={`batch-delete-${b.id}`}>
                        <Trash size={12} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>)}
      </div>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(v) => { setUploadOpen(v); if (!v) resetUploadDialog(); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Bulk Transaction Upload</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Select File (.xlsx / .csv) *</Label>
              <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { const f = e.target.files?.[0]; setUploadFile(f || null); if (f) doPreview(f); }} data-testid="bulk-file-input" />
            </div>
            {previewData && (
              <>
                <div className="bg-slate-50 border rounded p-3 text-xs space-y-1">
                  <div className="font-bold text-gray-800 flex items-center justify-between">
                    <span>Preview — {previewData.file_name}</span>
                    <span className="text-[10px] text-gray-500 font-normal">Total {previewData.row_count} rows parsed</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><div className="text-emerald-700 font-bold">{previewData.success_count}</div><div className="text-[10px]">Success · {fmt(previewData.success_amount)}</div></div>
                    <div><div className="text-amber-700 font-bold">{previewData.pending_count}</div><div className="text-[10px]">Pending · {fmt(previewData.pending_amount)}</div></div>
                    <div><div className="text-rose-700 font-bold">{previewData.fail_count}</div><div className="text-[10px]">Failed · {fmt(previewData.fail_amount)}</div></div>
                  </div>
                  <div className="border-t pt-1 flex justify-between font-semibold">
                    <span className="text-emerald-700">Credit: {fmt(previewData.credit_amount)}</span>
                    <span className="text-rose-700">Debit: {fmt(previewData.debit_amount)}</span>
                  </div>
                </div>

                {/* Full row-by-row verification table */}
                <div className="border rounded overflow-hidden">
                  <div className="bg-amber-50 border-b border-amber-200 px-3 py-1.5 flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs font-bold text-amber-900">
                      📋 Verify every row — confirm karne ke baad ledger post ho jayega
                      <span className="ml-2 font-normal text-amber-800">({previewData.row_count} rows total)</span>
                    </div>
                    <div className="flex gap-1 items-center">
                      <Input value={uploadRowSearch} onChange={(e) => setUploadRowSearch(e.target.value)} placeholder="Search TxnID / Mobile / Operator…" className="h-7 text-xs w-56" data-testid="preview-search" />
                      <select value={uploadRowFilter} onChange={(e) => setUploadRowFilter(e.target.value)} className="h-7 text-xs px-2 border rounded bg-white" data-testid="preview-status-filter">
                        <option value="all">All Status</option>
                        <option value="success">Success</option>
                        <option value="pending">Pending</option>
                        <option value="fail">Failed</option>
                      </select>
                    </div>
                  </div>
                  <div className="overflow-x-auto max-h-[60vh] overflow-y-auto" data-testid="preview-rows-container">
                    <table className="w-full text-[11px]">
                      <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0 z-10">
                        <tr>
                          <th className="px-2 py-1.5 text-left w-8">#</th>
                          <th className="px-2 py-1.5 text-left">TxnID</th>
                          <th className="px-2 py-1.5 text-left">Date</th>
                          <th className="px-2 py-1.5 text-left">Consumer / Mobile</th>
                          <th className="px-2 py-1.5 text-left">Operator</th>
                          <th className="px-2 py-1.5 text-right">Amount</th>
                          <th className="px-2 py-1.5 text-left">Status</th>
                          <th className="px-2 py-1.5 text-left">Remark</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const q = uploadRowSearch.trim().toLowerCase();
                          const filtered = (previewData.rows || []).filter((r) => {
                            if (uploadRowFilter !== 'all' && r.status !== uploadRowFilter) return false;
                            if (!q) return true;
                            return (
                              (r.txn_id || '').toLowerCase().includes(q) ||
                              (r.mobile || '').toLowerCase().includes(q) ||
                              (r.consumer_id || '').toLowerCase().includes(q) ||
                              (r.operator || '').toLowerCase().includes(q)
                            );
                          });
                          if (filtered.length === 0) return (
                            <tr><td colSpan={8} className="text-center text-xs text-gray-400 py-4">No matching rows</td></tr>
                          );
                          return filtered.map((r, i) => (
                            <tr key={`${r.txn_id}-${i}`} className="border-t hover:bg-amber-50/40">
                              <td className="px-2 py-1 text-gray-400">{i + 1}</td>
                              <td className="px-2 py-1 font-mono">{r.txn_id}</td>
                              <td className="px-2 py-1">{fmtDate(r.txn_date)}</td>
                              <td className="px-2 py-1">{r.consumer_id || r.mobile}</td>
                              <td className="px-2 py-1 truncate max-w-[180px]">{r.operator}</td>
                              <td className="px-2 py-1 text-right font-semibold">{fmt(r.amount)}</td>
                              <td className="px-2 py-1"><StatusChip status={r.status} /></td>
                              <td className="px-2 py-1 truncate max-w-[140px] text-gray-500">{r.remark || '—'}</td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Upload Date *</Label>
                <Input type="date" max={new Date().toISOString().slice(0, 10)} value={uploadDate} onChange={(e) => setUploadDate(e.target.value)} data-testid="bulk-upload-date" />
              </div>
              <div>
                <Label>Remarks (optional)</Label>
                <Input value={uploadRemarks} onChange={(e) => setUploadRemarks(e.target.value)} placeholder="e.g. R2 Ajay 21-Feb batch" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadOpen(false); resetUploadDialog(); }}>Cancel</Button>
            <Button onClick={submitUpload} disabled={!previewData || uploading} className="bg-amber-500 hover:bg-amber-600" data-testid="bulk-upload-confirm">
              {uploading ? 'Uploading…' : `Confirm & Post ${previewData?.row_count || ''} Rows to Ledger`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Row browser dialog */}
      <Dialog open={!!browseBatch} onOpenChange={(v) => { if (!v) setBrowseBatch(null); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{browseBatch?.file_name} — {browseBatch?.total_rows} rows</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={rowSearchInput} onChange={(e) => setRowSearchInput(e.target.value)} placeholder="Search TxnID / Mobile / Consumer / Operator…" className="pl-8" data-testid="row-search" />
            </div>
            <select value={rowStatus} onChange={(e) => setRowStatus(e.target.value)} className="h-10 px-3 border rounded bg-white text-sm" data-testid="row-status-filter">
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="pending">Pending</option>
              <option value="fail">Failed</option>
            </select>
          </div>
          <div className="overflow-x-auto border rounded">
            {rowsLoading ? <div className="p-4 text-center text-sm">Loading…</div> :
             rows.length === 0 ? <div className="p-4 text-center text-sm text-gray-500">No rows match</div> :
            (<table className="w-full text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                <tr>
                  <th className="px-2 py-1.5 text-left">TxnID</th>
                  <th className="px-2 py-1.5 text-left">Date</th>
                  <th className="px-2 py-1.5 text-left">Consumer / Mobile</th>
                  <th className="px-2 py-1.5 text-left">Operator</th>
                  <th className="px-2 py-1.5 text-right">Amount</th>
                  <th className="px-2 py-1.5 text-left">Status</th>
                  <th className="px-2 py-1.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={`border-t hover:bg-gray-50 ${r.refunded_at ? 'bg-rose-50/50' : ''}`} data-testid={`row-${r.id}`}>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{r.txn_id}</td>
                    <td className="px-2 py-1.5">{fmtDate(r.txn_date)}</td>
                    <td className="px-2 py-1.5">{r.consumer_id || r.mobile}</td>
                    <td className="px-2 py-1.5 truncate max-w-[180px]">{r.operator}</td>
                    <td className="px-2 py-1.5 text-right font-semibold">{fmt(r.amount)}</td>
                    <td className="px-2 py-1.5"><StatusChip status={r.status} /></td>
                    <td className="px-2 py-1.5 text-right">
                      {r.refunded_at ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 font-semibold">
                          🔒 Adjusted {fmtDate(r.refunded_at)} · {r.refunded_by}
                        </span>
                      ) : r.status !== 'fail' ? (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => { setRefundRow(r); setRefundForm({ amount: r.amount, reason: '' }); }} className="h-6 text-[10px]" data-testid={`refund-${r.id}`}><Receipt size={11} /></Button>
                          {showDelete && <Button size="sm" variant="outline" onClick={() => deleteRow(r)} className="h-6 text-[10px] text-rose-600 border-rose-300" data-testid={`delete-row-${r.id}`}><Trash size={11} /></Button>}
                        </div>
                      ) : <span className="text-[10px] text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>)}
          </div>
        </DialogContent>
      </Dialog>

      {/* Refund dialog */}
      <Dialog open={!!refundRow} onOpenChange={(v) => { if (!v) setRefundRow(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Refund Transaction</DialogTitle></DialogHeader>
          <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
            <div>TxnID: <b>{refundRow?.txn_id}</b></div>
            <div>Consumer: <b>{refundRow?.consumer_id || refundRow?.mobile}</b></div>
            <div>Amount: <b>{fmt(refundRow?.amount)}</b></div>
          </div>
          <div>
            <Label>Refund Amount</Label>
            <Input type="number" step="0.01" value={refundForm.amount} onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} data-testid="refund-amount" />
          </div>
          <div>
            <Label>Reason *</Label>
            <Input value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} placeholder="e.g. Consumer complaint verified via WhatsApp on 20-Jul" data-testid="refund-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundRow(null)}>Cancel</Button>
            <Button onClick={doRefund} disabled={refunding} className="bg-rose-600 hover:bg-rose-700" data-testid="refund-confirm">
              {refunding ? 'Processing…' : 'Process Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusChip({ status }) {
  const S = STATUS_STYLES[status] || STATUS_STYLES.unknown;
  const Icon = S.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded ${S.cls}`}>
      <Icon size={10} weight="fill" /> {status.toUpperCase()}
    </span>
  );
}
